import { isAxiosError } from 'axios';

import { IUnauthorizedErrorContext } from '../subjects/auth';

/** Cap the stored response body so a verbose backend error can't bloat the event. */
const MAX_BODY_LENGTH = 512;

/**
 * Decode the payload claims of a JWT *without verifying it*. We only read a few
 * non-sensitive claims for diagnostics, so an unverified decode is fine.
 * Returns `{}` on any malformed input.
 */
const decodeJwtClaims = (token: string): Record<string, unknown> => {
	try {
		const payload = token.split('.')[1];
		if (!payload) {
			return {};
		}
		const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
		const json =
			typeof atob === 'function'
				? atob(normalized)
				: Buffer.from(normalized, 'base64').toString('binary');
		return JSON.parse(json);
	} catch {
		return {};
	}
};

const stripQuery = (url?: string): string | undefined => {
	if (!url) {
		return undefined;
	}
	const queryIndex = url.indexOf('?');
	return queryIndex === -1 ? url : url.slice(0, queryIndex);
};

const truncateBody = (value: unknown): string | undefined => {
	if (value === undefined || value === null) {
		return undefined;
	}
	let str: string;
	if (typeof value === 'string') {
		str = value;
	} else {
		try {
			str = JSON.stringify(value);
		} catch {
			str = String(value);
		}
	}
	return str.length > MAX_BODY_LENGTH
		? `${str.slice(0, MAX_BODY_LENGTH)}…`
		: str;
};

const readAuthHeader = (headers: unknown): string | undefined => {
	if (!headers || typeof headers !== 'object') {
		return undefined;
	}
	const record = headers as Record<string, unknown>;
	const raw = record['Authorization'] ?? record['authorization'];
	return typeof raw === 'string' ? raw : undefined;
};

/**
 * Build the diagnostic context for a 401 response: the request method/path,
 * whether a bearer was attached, and the attached token's non-sensitive claims
 * (never the token itself) plus a truncated response body. See
 * {@link IUnauthorizedErrorContext}.
 */
export const buildUnauthorizedContext = (
	error: unknown
): IUnauthorizedErrorContext => {
	if (!isAxiosError(error)) {
		return { hadBearer: false };
	}

	const authHeader = readAuthHeader(error.config?.headers);
	const token = authHeader?.replace(/^Bearer\s+/i, '');
	const hadBearer = Boolean(token);
	const claims = token ? decodeJwtClaims(token) : {};

	return {
		method: error.config?.method?.toUpperCase(),
		path: stripQuery(error.config?.url),
		hadBearer,
		tokenExp: typeof claims['exp'] === 'number' ? claims['exp'] : undefined,
		tokenAuthTime:
			typeof claims['auth_time'] === 'number' ? claims['auth_time'] : undefined,
		tokenUserId:
			typeof claims['user_id'] === 'string'
				? claims['user_id']
				: typeof claims['sub'] === 'string'
				? claims['sub']
				: undefined,
		responseBody: truncateBody(error.response?.data),
	};
};
